import { Close, NBY, Shoe } from '@nike/nike-design-system-icons';
import classnames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RANKING_RULE_TARGET_TYPES } from 'constants/index';
import { JapaneseYen, Shapes } from 'lucide-react';
import { useMemo } from 'react';
import { IconButton } from './IconButton';

export default function SortableTargetItem({
  id,
  data,
  actionType,
  onRemove,
}: {
  id: string;
  data: RankingRuleTargetBoostItem | RankingRuleTargetBuryItem;
  actionType: string;
  onRemove: VoidFunction;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const clx = classnames(
    'tw-border-solid tw-flex tw-items-center tw-justify-between tw-rounded-full tw-border tw-text-sm tw-py-1 tw-px-2',
    {
      'tw-text-green-700 tw-border-green-700 tw-bg-green-200/50':
        data.type === RANKING_RULE_TARGET_TYPES.STYLE_COLOR,
      'tw-text-neutral-800 tw-border-neutral-400 tw-bg-neutral-100':
        data.type === RANKING_RULE_TARGET_TYPES.STYLE_CODE,
      'tw-text-zinc-800 tw-border-zinc-400 tw-bg-white':
        data.type === RANKING_RULE_TARGET_TYPES.PBID,
      'tw-text-blue-800 tw-border-blue-400 tw-bg-blue-100':
        data.type === RANKING_RULE_TARGET_TYPES.TAXONOMY,
      'tw-text-purple-800 tw-border-purple-400 tw-bg-purple-100':
        data.type === RANKING_RULE_TARGET_TYPES.PRICE,
    },
  );

  const text = useMemo(() => {
    if (data.type === RANKING_RULE_TARGET_TYPES.PRICE) {
      const { multiValue, value, relation } = data;
      if (relation === 'range') {
        return `${multiValue?.[0] || '?'} < price < ${multiValue?.[1] || '?'}`;
      } else if (relation === 'lt') {
        return `price < ${value}`;
      } else if (relation === 'gt') {
        return `price > ${value}`;
      }
    }
    return data.label || data.value;
  }, [data]);

  const icon = useMemo(() => {
    if (data.type === RANKING_RULE_TARGET_TYPES.PRICE) {
      return <JapaneseYen size={16} className='tw-m-1' />;
    }
    if (data.type === RANKING_RULE_TARGET_TYPES.TAXONOMY) {
      return <Shapes size={16} className='tw-m-1' />;
    }
    if (
      data.type === RANKING_RULE_TARGET_TYPES.STYLE_COLOR ||
      data.type === RANKING_RULE_TARGET_TYPES.STYLE_CODE
    ) {
      return <Shoe size='s' />;
    }
    if (data.type === RANKING_RULE_TARGET_TYPES.PBID) {
      return <NBY size='s' title='by PBID' />;
    }
  }, [data.type]);

  return (
    <div className='tw-group tw-relative'>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={clx}
      >
        {icon}
        <div className='tw-px-0.5'>{text}</div>
      </div>
      <IconButton
        className='tw-invisible group-hover:tw-visible tw-absolute tw-top-1 tw-right-1 tw-z-10'
        label='Remove'
        icon={<Close width={16} height={16} />}
        size='sm'
        onClick={onRemove}
        style={{ transform: 'translate(50%, -50%)' }}
      />
    </div>
  );
}
